// src/pages/UserProfile.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { AnimatePresence, motion } from "framer-motion";
import {
    FaUserCircle,
    FaPhoneAlt,
    FaEnvelope,
    FaCopy,
    FaEye,
    FaEyeSlash,
    FaCheckCircle,
    FaTimesCircle,
    FaLink,
    FaArrowLeft,
    FaSyncAlt,
    FaChartBar,
    FaCoins,
    FaWallet,
    FaUserTie,
    FaInfoCircle,
    FaMoneyBillWave,
    FaExchangeAlt,
    FaUsers,
    FaKey,
    FaAddressCard,
    FaSave,
    FaLock,
    FaChevronDown,
    FaChevronUp,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * API:
 * POST `${BASE_ENDPOINT}/admin/user-profile`
 * payload: { username, token }
 *
 * NEW:
 * POST `${BASE_ENDPOINT}/admin/user-deposits`
 * payload: { token, username, page_no }
 *
 * POST `${BASE_ENDPOINT}/admin/user-withdrawals`
 * payload: { token, username, page_no }
 */

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 0) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function formatDateTime(v) {
    if (!v) return "—";
    const dt = new Date(v);
    if (!Number.isFinite(dt.getTime())) return safeText(v);
    return dt.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) { }

    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied", disabled }) {
    const isDisabled =
        disabled || value === null || value === undefined || value === "" || value === "—";

    const onCopy = async () => {
        const ok = await copyToClipboard(String(value));
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };

    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary px-2 py-1"
            onClick={onCopy}
            disabled={isDisabled}
            title="Copy"
            style={{ fontSize: "0.75rem" }}
        >
            <FaCopy />
        </button>
    );
}

function StatTile({ icon, title, value, subtitle, color = "primary" }) {
    const colorClasses = {
        primary: "bg-primary text-white",
        success: "bg-success text-white",
        warning: "bg-warning text-dark",
        info: "bg-info text-white",
        danger: "bg-danger text-white",
        secondary: "bg-secondary text-white",
    };

    return (
        <div className="col-6 col-md-4 col-lg-3">
            <div className={`card border-0 shadow-sm h-100 ${colorClasses[color] || colorClasses.primary}`}>
                <div className="card-body p-2">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                        <div className="fw-bold" style={{ fontSize: "0.8rem" }}>
                            {title}
                        </div>
                        <div style={{ fontSize: "1rem" }}>{icon}</div>
                    </div>
                    <div className="h6 fw-bold mb-0">{value}</div>
                    {subtitle ? (
                        <div className="small opacity-75 mt-1" style={{ fontSize: "0.7rem" }}>{subtitle}</div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Collapsible Section Wrapper ---------------- */
function SectionCard({
    title,
    icon,
    open,
    onToggle,
    children,
    right,
}) {
    return (
        <div className="card profile-card mb-3">
            <div className="card-body p-3">
                <div
                    className="section-header d-flex align-items-center justify-content-between cursor-pointer"
                    onClick={onToggle}
                    style={{ cursor: "pointer" }}
                >
                    <div>
                        <h5 className="fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "1rem" }}>
                            <span className="text-primary">{icon}</span>
                            {title}
                        </h5>
                    </div>

                    <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {open && right}
                        <span className="text-muted">
                            {open ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                        </span>
                    </div>
                </div>

                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ overflow: "hidden" }}
                        >
                            <div className="pt-2">{children}</div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

/* ---------------- Status helpers (updated per instruction) ---------------- */
function statusMeta(status) {
    const s = String(status ?? "");
    if (s === "1") {
        return { label: "Success", badge: "bg-success", icon: <FaCheckCircle className="me-1" /> };
    }
    if (s === "2") {
        return { label: "Rejected", badge: "bg-danger", icon: <FaTimesCircle className="me-1" /> };
    }
    // 0 or anything else => Pending
    return { label: "Pending", badge: "bg-warning text-dark", icon: null };
}

function StatusBadge({ status }) {
    const meta = statusMeta(status);
    return (
        <span className={`badge ${meta.badge} px-2 py-1 d-inline-flex align-items-center`} style={{ fontSize: "0.75rem" }}>
            {meta.icon}
            {meta.label}
        </span>
    );
}

/* ---------------- Team Network Component for Profile Section ---------------- */
function TeamNetworkProfile({ team }) {
    if (!team) return null;

    const totalTeamMembers = Object.values(team).reduce((sum, count) => sum + (Number(count) || 0), 0);

    const teamLevels = Object.entries(team)
        .filter(([key]) => key.startsWith("level_"))
        .map(([key, count]) => {
            const level = parseInt(key.replace("level_", ""), 10);
            const levelCount = Number(count) || 0;
            return { level, count: levelCount };
        })
        .sort((a, b) => a.level - b.level);

    const activeLevels = teamLevels.filter((lvl) => lvl.count > 0).length;

    return (
        <div className="mt-3">
            <h6 className="fw-bold text-primary mb-2 d-flex align-items-center" style={{ fontSize: "0.9rem" }}>
                <FaUsers className="me-1" size={12} />
                Team Network
            </h6>

            <div className="card border-0 bg-light">
                <div className="card-body p-2">
                    {/* Summary Stats */}
                    <div className="row g-2 mb-3">
                        <div className="col-6">
                            <div className="text-center">
                                <div className="text-muted small" style={{ fontSize: "0.7rem" }}>Total Team</div>
                                <div className="h5 fw-bold text-primary mb-0">{nfmt(totalTeamMembers)}</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-center">
                                <div className="text-muted small" style={{ fontSize: "0.7rem" }}>Active Levels</div>
                                <div className="h5 fw-bold text-success mb-0">{activeLevels}</div>
                            </div>
                        </div>
                    </div>

                    {/* Level Grid */}
                    <div className="row g-1">
                        {teamLevels.map(({ level, count }) => (
                            <div key={level} className="col-3 col-sm-2">
                                <div className="text-center">
                                    <div className={`rounded-circle mx-auto d-flex align-items-center justify-content-center ${count > 0 ? "bg-primary text-white" : "bg-light text-muted"}`}
                                        style={{ width: "30px", height: "30px", fontSize: "0.8rem" }}>
                                        {level}
                                    </div>
                                    <div className="small text-muted mt-1" style={{ fontSize: "0.65rem" }}>L{level}</div>
                                    <div className={`small fw-bold ${count > 0 ? "text-dark" : "text-muted"}`}>
                                        {nfmt(count)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Progress Bar for Visualization */}
                    {totalTeamMembers > 0 && (
                        <div className="mt-3">
                            <div className="d-flex justify-content-between mb-1">
                                <div className="small text-muted" style={{ fontSize: "0.7rem" }}>Team Distribution</div>
                                <div className="small text-muted" style={{ fontSize: "0.7rem" }}>{activeLevels}/10 levels</div>
                            </div>
                            <div className="progress" style={{ height: "6px" }}>
                                <div
                                    className="progress-bar bg-primary"
                                    style={{ width: `${(activeLevels / 10) * 100}%` }}
                                    role="progressbar"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Password Change Form ---------------- */
function PasswordChangeForm({ username, onUpdateSuccess }) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: "",
    });

    const { logout } = useAuth();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }

        if (formData.newPassword.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/change-user-password`,
                headers: { "Content-Type": "application/json" },
                data: {
                    username,
                    new_password: formData.newPassword,
                    confirm_password: formData.confirmPassword,
                    token: localStorage.getItem("token") || "",
                },
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    logout();
                    return;
                }
                toast.error(data?.error || "Failed to update password");
                return;
            }

            if (data?.msg) {
                toast.success(data?.msg);
                setFormData({ newPassword: "", confirmPassword: "" });
                if (onUpdateSuccess) onUpdateSuccess();
            }
        } catch (err) {
            console.log(err);
            toast.error("Failed to update password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-3">
            <h6 className="fw-bold text-primary d-flex align-items-center gap-1 mb-2" style={{ fontSize: "0.9rem" }}>
                <FaKey size={12} /> Change Password
            </h6>
            <form onSubmit={handleSubmit}>
                <div className="mb-2">
                    <label className="form-label small" style={{ fontSize: "0.8rem" }}>New Password</label>
                    <input
                        type="password"
                        name="newPassword"
                        className="form-control form-control-sm"
                        value={formData.newPassword}
                        onChange={handleChange}
                        placeholder="Enter new password"
                        required
                        style={{ fontSize: "0.85rem" }}
                    />
                </div>
                <div className="mb-2">
                    <label className="form-label small" style={{ fontSize: "0.8rem" }}>Confirm Password</label>
                    <input
                        type="password"
                        name="confirmPassword"
                        className="form-control form-control-sm"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm new password"
                        required
                        style={{ fontSize: "0.85rem" }}
                    />
                </div>
                <button
                    type="submit"
                    className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
                    disabled={loading}
                    style={{ fontSize: "0.85rem" }}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm"></span>
                            Updating...
                        </>
                    ) : (
                        <>
                            <FaSave size={12} /> Update Password
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}

/* ---------------- Withdrawal Wallet Form ---------------- */
function WithdrawalWalletForm({ username, onUpdateSuccess }) {
    const [loading, setLoading] = useState(false);
    const [walletAddress, setWalletAddress] = useState("");

    const { logout } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!walletAddress.trim()) {
            toast.error("Please enter wallet address");
            return;
        }

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/update-user-wallet-address`,
                headers: { "Content-Type": "application/json" },
                data: {
                    username: username || "",
                    address: walletAddress || "",
                    token: localStorage.getItem("token") || "",
                },
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    logout();
                    return;
                }
                toast.error(data?.error);
                return;
            }

            if (data?.msg) {
                toast.success(data?.msg);
                setWalletAddress("");
                if (onUpdateSuccess) onUpdateSuccess();
            }
        } catch (err) {
            console.log(err);
            toast.error("Failed to update wallet address");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-3">
            <h6 className="fw-bold text-primary d-flex align-items-center gap-1 mb-2" style={{ fontSize: "0.9rem" }}>
                <FaAddressCard size={12} /> Update Withdrawal Wallet
            </h6>
            <form onSubmit={handleSubmit}>
                <div className="mb-2">
                    <label className="form-label small" style={{ fontSize: "0.8rem" }}>New Wallet Address</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                        placeholder="Enter new withdrawal wallet address"
                        required
                        style={{ fontSize: "0.85rem" }}
                    />
                </div>
                <button
                    type="submit"
                    className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
                    disabled={loading}
                    style={{ fontSize: "0.85rem" }}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm"></span>
                            Updating...
                        </>
                    ) : (
                        <>
                            <FaSave size={12} /> Update Wallet
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}

/* ---------------- Wallet Address Display Component ---------------- */
function WalletAddressDisplay({ walletAddress }) {
    const [showAddress, setShowAddress] = useState(false);

    if (!walletAddress || walletAddress === "—") return null;

    return (
        <div className="mt-3">
            <h6 className="fw-bold text-primary d-flex align-items-center gap-1 mb-2" style={{ fontSize: "0.9rem" }}>
                <FaAddressCard size={12} /> Current Wallet Address
            </h6>
            <div className="card border-0 bg-light">
                <div className="card-body p-2">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                        <div className="text-muted small" style={{ fontSize: "0.8rem" }}>Withdrawal Wallet</div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary px-2 py-1"
                            onClick={() => setShowAddress(!showAddress)}
                            title={showAddress ? "Hide address" : "Show address"}
                        >
                            {showAddress ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                        </button>
                    </div>
                    {showAddress ? (
                        <div className="mt-1">
                            <div className="d-flex align-items-center gap-1">
                                <code
                                    className="bg-white p-2 rounded border flex-grow-1"
                                    style={{ wordBreak: "break-word", fontSize: "0.8rem" }}
                                >
                                    {walletAddress}
                                </code>
                                <CopyBtn value={walletAddress} label="Wallet address copied" />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-1">
                            <span className="text-muted" style={{ fontSize: "0.85rem" }}>••••••••••••••••••••••••••••••</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Upline Modal ---------------- */
function UplineModal({ open, upline, onClose }) {
    const [showPass, setShowPass] = useState(false);

    useEffect(() => {
        if (open) setShowPass(false);
    }, [open]);

    if (!upline) return null;

    const mobileDisplay =
        safeText(upline?.mobile) === "—"
            ? "—"
            : `+${safeText(upline?.country_code) === "—" ? "" : safeText(upline?.country_code)} ${safeText(
                upline?.mobile
            )}`.trim();

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1040,
                        }}
                    />

                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1050,
                            overflowY: "auto",
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered modal-lg"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "800px" }}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                                <div
                                    className="modal-header border-bottom-0 p-3"
                                    style={{
                                        backgroundColor: "#f8f9fa",
                                        borderTopLeftRadius: "12px",
                                        borderTopRightRadius: "12px",
                                    }}
                                >
                                    <div className="d-flex align-items-center w-100">
                                        <div className="flex-grow-1">
                                            <h5 className="modal-title fw-bold text-dark mb-0" style={{ fontSize: "1.1rem" }}>
                                                <FaUserTie className="me-2 text-primary" />
                                                Upline Details
                                            </h5>
                                        </div>
                                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                                    </div>
                                </div>

                                <div className="modal-body p-0">
                                    <div className="p-3">
                                        <div className="row g-2">
                                            <div className="col-12 col-md-6">
                                                <div className="card border-0 bg-light h-100">
                                                    <div className="card-body p-2">
                                                        <h6 className="fw-bold text-primary mb-2" style={{ fontSize: "0.95rem" }}>Basic Information</h6>
                                                        <div className="mb-2">
                                                            <div className="text-muted small" style={{ fontSize: "0.8rem" }}>Full Name</div>
                                                            <div className="fw-semibold mt-1" style={{ fontSize: "0.9rem" }}>{safeText(upline?.name)}</div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <div className="text-muted small" style={{ fontSize: "0.8rem" }}>Refer Code</div>
                                                            <div className="d-flex align-items-center gap-1 mt-1">
                                                                <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>{safeText(upline?.refer_code)}</span>
                                                                <CopyBtn value={upline?.refer_code} label="Refer code copied" />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="text-muted small" style={{ fontSize: "0.8rem" }}>Status</div>
                                                            <div className="mt-1">
                                                                {upline?.status ? (
                                                                    <span className="badge bg-success d-inline-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "0.8rem" }}>
                                                                        <FaCheckCircle size={10} /> Active
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-danger d-inline-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "0.8rem" }}>
                                                                        <FaTimesCircle size={10} /> Inactive
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <div className="card border-0 bg-light h-100">
                                                    <div className="card-body p-2">
                                                        <h6 className="fw-bold text-primary mb-2" style={{ fontSize: "0.95rem" }}>Contact Information</h6>
                                                        <div className="mb-2">
                                                            <div className="text-muted small" style={{ fontSize: "0.8rem" }}>Mobile</div>
                                                            <div className="d-flex align-items-center gap-1 mt-1">
                                                                <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>{mobileDisplay}</span>
                                                                <CopyBtn value={upline?.mobile || ""} label="Mobile copied" disabled={!upline?.mobile} />
                                                            </div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <div className="text-muted small" style={{ fontSize: "0.8rem" }}>Email</div>
                                                            <div className="d-flex align-items-center gap-1 mt-1">
                                                                <span className="fw-semibold" style={{ wordBreak: "break-word", fontSize: "0.9rem" }}>
                                                                    {safeText(upline?.email)}
                                                                </span>
                                                                <CopyBtn value={upline?.email || ""} label="Email copied" disabled={!upline?.email} />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <div className="text-muted small mb-1" style={{ fontSize: "0.8rem" }}>Password</div>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary btn-sm px-2 py-1"
                                                                    onClick={() => setShowPass((v) => !v)}
                                                                    title={showPass ? "Hide password" : "Show password"}
                                                                >
                                                                    {showPass ? <FaEyeSlash size={10} /> : <FaEye size={10} />}
                                                                </button>
                                                                {showPass ? (
                                                                    <>
                                                                        <span className="fw-semibold" style={{ wordBreak: "break-word", fontSize: "0.9rem" }}>
                                                                            {safeText(upline?.password)}
                                                                        </span>
                                                                        <CopyBtn
                                                                            value={upline?.password || ""}
                                                                            label="Password copied"
                                                                            disabled={!upline?.password}
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted small" style={{ fontSize: "0.85rem" }}>••••••••</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="modal-footer border-top-0 p-3"
                                    style={{
                                        backgroundColor: "#f8f9fa",
                                        borderBottomLeftRadius: "12px",
                                        borderBottomRightRadius: "12px",
                                    }}
                                >
                                    <button className="btn btn-secondary px-3" type="button" onClick={onClose} style={{ fontSize: "0.85rem" }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

/* ---------------- Password Row Component ---------------- */
function PasswordRow({ password }) {
    const [show, setShow] = useState(false);

    useEffect(() => {
        setShow(false);
    }, [password]);

    const pwd = safeText(password);

    return (
        <div className="d-flex align-items-center justify-content-between p-2 border-top">
            <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.9rem" }}>
                <FaLock size={12} /> Password
            </div>
            <div className="d-flex align-items-center gap-1">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary px-2 py-1"
                    onClick={() => setShow((v) => !v)}
                    title={show ? "Hide password" : "Show password"}
                >
                    {show ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                </button>

                {show && (
                    <>
                        <code className="bg-light p-1 rounded border" style={{ maxWidth: "150px", wordBreak: "break-word", fontSize: "0.8rem" }}>
                            {pwd}
                        </code>
                        <CopyBtn value={pwd === "—" ? "" : pwd} label="Password copied" disabled={pwd === "—"} />
                    </>
                )}
            </div>
        </div>
    );
}

/* ---------------- History Details Modal ---------------- */
function HistoryDetailModal({ open, type, item, onClose }) {
    if (!open || !item) return null;

    const isRejected = String(item?.status) === "2";

    const rows =
        type === "deposit"
            ? [
                { label: "Date", value: formatDateTime(item?.date) },
                { label: "Amount", value: nfmt(item?.amount, 2) },
                { label: "Method", value: safeText(item?.method) },
                { label: "Reference No", value: safeText(item?.ref_no) },
                { label: "Status", value: <StatusBadge status={item?.status} /> },
                { label: "Remark", value: safeText(item?.remark) },
                ...(isRejected ? [{ label: "Reject Reason", value: safeText(item?.reject_reason) }] : []),
            ]
            : [
                { label: "Date", value: formatDateTime(item?.date) },
                { label: "Amount", value: nfmt(item?.amount, 2) },
                { label: "Status", value: <StatusBadge status={item?.status} /> },
                { label: "Remark", value: safeText(item?.remark) },
                ...(isRejected ? [{ label: "Reject Reason", value: safeText(item?.reject_reason) }] : []),
            ];

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1040,
                        }}
                    />

                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1050,
                            overflowY: "auto",
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "720px" }}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                                <div className="modal-header border-bottom-0 p-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <div className="d-flex align-items-center w-100">
                                        <div className="flex-grow-1">
                                            <h5 className="modal-title fw-bold text-dark mb-0" style={{ fontSize: "1.1rem" }}>
                                                <FaInfoCircle className="me-2 text-primary" />
                                                {type === "deposit" ? "Deposit Details" : "Withdrawal Details"}
                                            </h5>
                                        </div>
                                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                                    </div>
                                </div>

                                <div className="modal-body p-3">
                                    <div className="card border-0 bg-light">
                                        <div className="card-body p-2">
                                            <div className="row g-2">
                                                {rows.map((r) => (
                                                    <div key={r.label} className="col-12 col-md-6">
                                                        <div className="text-muted small" style={{ fontSize: "0.8rem" }}>{r.label}</div>
                                                        <div className="fw-semibold mt-1" style={{ wordBreak: "break-word", fontSize: "0.9rem" }}>
                                                            {r.value}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer border-top-0 p-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <button className="btn btn-secondary px-3" type="button" onClick={onClose} style={{ fontSize: "0.85rem" }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

/* ---------------- Main Page Component ---------------- */
export default function UserProfile() {
    const { username } = useParams();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        profile: null,
        trade: { count: { total: 0, win: 0, loss: 0 }, value: { total: 0, win: 0, loss: 0, profit: 0 } },
        transaction: { deposit: 0, withdrawl: 0 },
        team: { level_1: 0, level_2: 0, level_3: 0, level_4: 0, level_5: 0, level_6: 0, level_7: 0, level_8: 0, level_9: 0, level_10: 0 },
        has_upline: false,
        upline: null,
        has_wallet_address: false,
        wallet_address: null,
    });

    const [uplineOpen, setUplineOpen] = useState(false);

    // Collapses (default collapsed as requested)
    const [openTrade, setOpenTrade] = useState(false);
    const [openTxn, setOpenTxn] = useState(false);
    const [openDeposits, setOpenDeposits] = useState(false);
    const [openWithdrawals, setOpenWithdrawals] = useState(false);

    // Deposit history state
    const [depLoading, setDepLoading] = useState(false);
    const [depRows, setDepRows] = useState([]);
    const [depPage, setDepPage] = useState(1);
    const [depMeta, setDepMeta] = useState({ total: 0, limit: 10 });
    const depHasMore = useMemo(() => {
        const total = Number(depMeta?.total || 0);
        const limit = Number(depMeta?.limit || 10);
        return depPage * limit < total;
    }, [depMeta, depPage]);

    // Withdrawal history state
    const [wdLoading, setWdLoading] = useState(false);
    const [wdRows, setWdRows] = useState([]);
    const [wdPage, setWdPage] = useState(1);
    const [wdMeta, setWdMeta] = useState({ total: 0, limit: 10 });
    const wdHasMore = useMemo(() => {
        const total = Number(wdMeta?.total || 0);
        const limit = Number(wdMeta?.limit || 10);
        return wdPage * limit < total;
    }, [wdMeta, wdPage]);

    // Details modal for history
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailType, setDetailType] = useState(null); // "deposit" | "withdrawal"
    const [detailItem, setDetailItem] = useState(null);

    const openDetails = (type, item) => {
        setDetailType(type);
        setDetailItem(item);
        setDetailOpen(true);
    };

    const closeDetails = () => {
        setDetailOpen(false);
        setDetailType(null);
        setDetailItem(null);
    };

    const fetchProfile = useCallback(async () => {
        if (!username) {
            setLoading(false);
            toast.error("Username missing in route parameter");
            return;
        }

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/user-profile`,
                headers: { "Content-Type": "application/json" },
                data: {
                    username,
                    token: localStorage.getItem("token") || "",
                },
            });

            const out = res?.data;

            if (out?.error) {
                if (out?.error === "session expired") {
                    logout();
                    return;
                }

                toast.error(typeof out?.error === "string" ? out.error : "Failed to load profile");
                setData((p) => ({
                    ...p,
                    profile: null,
                    upline: null,
                    has_upline: false,
                    has_wallet_address: false,
                    wallet_address: null,
                }));
                return;
            }

            setData({
                profile: out?.profile || null,
                trade: out?.trade || { count: { total: 0, win: 0, loss: 0 }, value: { total: 0, win: 0, loss: 0, profit: 0 } },
                transaction: out?.transaction || { deposit: 0, withdrawl: 0 },
                team:
                    out?.team || {
                        level_1: 0,
                        level_2: 0,
                        level_3: 0,
                        level_4: 0,
                        level_5: 0,
                        level_6: 0,
                        level_7: 0,
                        level_8: 0,
                        level_9: 0,
                        level_10: 0,
                    },
                has_upline: !!out?.has_upline,
                upline: out?.upline || null,
                has_wallet_address: !!out?.has_wallet_address,
                wallet_address: out?.wallet_address || null,
            });
        } catch (err) {
            console.log(err);
            toast.error("Failed to load profile");
            setData((p) => ({
                ...p,
                profile: null,
                upline: null,
                has_upline: false,
                has_wallet_address: false,
                wallet_address: null,
            }));
        } finally {
            setLoading(false);
        }
    }, [username, logout]);

    const fetchDeposits = useCallback(
        async ({ page = 1, append = false } = {}) => {
            if (!username) return;
            setDepLoading(true);
            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/user-deposits`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token") || "",
                        username,
                        page_no: page,
                    },
                });

                const out = res?.data;

                if (out?.error) {
                    if (out?.error === "session expired") {
                        logout();
                        return;
                    }
                    toast.error(typeof out?.error === "string" ? out?.error : "Failed to load deposit history");
                    return;
                }

                const rows = Array.isArray(out?.data) ? out.data : [];
                const meta = out?.meta || { total: rows.length, limit: 10 };

                setDepMeta({
                    total: Number(meta?.total || 0),
                    limit: Number(meta?.limit || 10),
                });

                setDepRows((prev) => (append ? [...prev, ...rows] : rows));
                setDepPage(page);
            } catch (e) {
                console.log(e);
                toast.error("Failed to load deposit history");
            } finally {
                setDepLoading(false);
            }
        },
        [username, logout]
    );

    const fetchWithdrawals = useCallback(
        async ({ page = 1, append = false } = {}) => {
            if (!username) return;
            setWdLoading(true);
            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/user-withdrawals`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token") || "",
                        username,
                        page_no: page,
                    },
                });

                const out = res?.data;

                if (out?.error) {
                    if (out?.error === "session expired") {
                        logout();
                        return;
                    }
                    toast.error(typeof out?.error === "string" ? out?.error : "Failed to load withdrawal history");
                    return;
                }

                const rows = Array.isArray(out?.data) ? out.data : [];
                const meta = out?.meta || { total: rows.length, limit: 10 };

                setWdMeta({
                    total: Number(meta?.total || 0),
                    limit: Number(meta?.limit || 10),
                });

                setWdRows((prev) => (append ? [...prev, ...rows] : rows));
                setWdPage(page);
            } catch (e) {
                console.log(e);
                toast.error("Failed to load withdrawal history");
            } finally {
                setWdLoading(false);
            }
        },
        [username, logout]
    );

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    useEffect(() => {
        // Load first page on open
        if (openDeposits) fetchDeposits({ page: 1, append: false });
        if (openWithdrawals) fetchWithdrawals({ page: 1, append: false });
    }, [username, openDeposits, openWithdrawals]);

    const profile = data?.profile;

    const mobileDisplay = useMemo(() => {
        const mobile = safeText(profile?.mobile);
        const cc = safeText(profile?.country_code);
        if (mobile === "—") return "—";
        return `+${cc === "—" ? "" : cc} ${mobile}`.trim();
    }, [profile]);

    const onRefresh = async () => {
        await fetchProfile();
        if (openDeposits) await fetchDeposits({ page: 1, append: false });
        if (openWithdrawals) await fetchWithdrawals({ page: 1, append: false });
    };

    const balanceStats = useMemo(
        () => [
            {
                icon: <FaCoins size={14} />,
                title: "Coin Total",
                value: nfmt(profile?.balance?.coin?.total, 2),
                color: "success",
            },
            {
                icon: <FaCoins size={14} />,
                title: "Coin Locked",
                value: nfmt(profile?.balance?.coin?.lock, 2),
                color: "warning",
            },
            {
                icon: <FaWallet size={14} />,
                title: "Net Coin",
                value: nfmt(Number(profile?.balance?.coin?.total || 0) - Number(profile?.balance?.coin?.lock || 0), 2),
                color: "primary",
            },
            {
                icon: <FaWallet size={14} />,
                title: "USDT Balance",
                value: nfmt(profile?.balance?.usdt, 2),
                color: "info",
            },
        ],
        [profile]
    );

    const tradeStats = useMemo(
        () => [
            {
                icon: <FaChartBar size={14} />,
                title: "Total Coin",
                value: nfmt(data?.trade?.value?.total, 2),
                color: "info",
            },
            {
                icon: <FaCheckCircle size={14} />,
                title: "Win Coin",
                value: nfmt(data?.trade?.value?.win, 2),
                color: "success",
            },
            {
                icon: <FaTimesCircle size={14} />,
                title: "Loss Coin",
                value: nfmt(data?.trade?.value?.loss, 2),
                color: "danger",
            },
            {
                icon: <FaChartBar size={14} />,
                title: "Net Profit",
                value: nfmt(data?.trade?.value?.profit, 2),
                color: "warning",
            },
        ],
        [data?.trade]
    );

    const transactionStats = useMemo(
        () => [
            {
                icon: <FaMoneyBillWave size={14} />,
                title: "Total Deposit",
                value: nfmt(data?.transaction?.deposit, 2),
                color: "success",
            },
            {
                icon: <FaExchangeAlt size={14} />,
                title: "Total Withdrawal",
                value: nfmt(data?.transaction?.withdrawl, 2),
                color: "warning",
            },
            {
                icon: <FaExchangeAlt size={14} />,
                title: "Net Transaction",
                value: nfmt(Number(data?.transaction?.deposit || 0) - Number(data?.transaction?.withdrawl || 0), 2),
                color: "info",
                subtitle: "Deposit - Withdrawal",
            },
        ],
        [data?.transaction]
    );

    return (
        <PageTransition>
            <style>{`
                .profile-card {
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    transition: transform 0.2s ease;
                }
                .profile-card:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .profile-avatar {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    font-size: 1.5rem;
                }
                .info-row {
                    border-bottom: 1px solid #e9ecef;
                    padding: 8px 0;
                    transition: background-color 0.2s ease;
                }
                .info-row:hover {
                    background-color: rgba(0,0,0,0.02);
                }
                .info-row:last-child {
                    border-bottom: none;
                }
                .section-header {
                    border-bottom: 1px solid #dee2e6;
                    padding-bottom: 8px;
                    margin-bottom: 0;
                }
                .table th, .table td {
                    padding: 0.5rem;
                    font-size: 0.85rem;
                }
                .table thead th {
                    font-size: 0.8rem;
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                @media (max-width: 768px) {
                    .profile-avatar {
                        width: 50px;
                        height: 50px;
                        font-size: 1.25rem;
                    }
                    .info-row {
                        padding: 6px 0;
                    }
                }
                @media (max-width: 576px) {
                    .card .card-body {
                        padding: 0.75rem;
                    }
                    .table th, .table td {
                        padding: 0.375rem;
                        font-size: 0.8rem;
                    }
                }
            `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <button
                            type="button"
                            className="btn btn-outline-secondary d-inline-flex align-items-center gap-1 px-2 py-1"
                            onClick={() => navigate(-1)}
                            title="Back"
                            style={{ fontSize: "0.85rem" }}
                        >
                            <FaArrowLeft size={12} /> Back
                        </button>
                        <h2 className="fw-bold mb-0" style={{ fontSize: "1.5rem" }}>User Profile</h2>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        type="button"
                        className="btn btn-primary d-inline-flex align-items-center gap-1 px-3 py-1"
                        onClick={onRefresh}
                        disabled={loading}
                        title="Refresh"
                        style={{ fontSize: "0.85rem" }}
                    >
                        <FaSyncAlt className={loading ? "fa-spin" : ""} size={12} />
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="card profile-card">
                    <div className="card-body">
                        <div className="placeholder-glow">
                            <div className="d-flex justify-content-center mb-3">
                                <div className="placeholder profile-avatar"></div>
                            </div>
                            <div className="row g-2">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="col-6 col-md-3">
                                        <div className="card border-0">
                                            <div className="card-body p-2">
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-6 mb-1"></span>
                                                    <span className="placeholder col-10"></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : !profile ? (
                <div className="card profile-card">
                    <div className="card-body text-center py-4">
                        <div className="d-flex flex-column align-items-center gap-2">
                            <div className="profile-avatar bg-light text-muted">
                                <FaUserCircle />
                            </div>
                            <div className="fw-bold fs-5 text-muted">No Profile Found</div>
                            <button type="button" className="btn btn-outline-primary mt-1" onClick={() => navigate(-1)} style={{ fontSize: "0.85rem" }}>
                                <FaArrowLeft /> Go Back
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    {/* Left Column: Profile Information */}
                    <div className="col-12 col-xl-4">
                        <div className="card profile-card h-100">
                            <div className="card-body p-3">
                                {/* Profile Header */}
                                <div className="text-center mb-3">
                                    <div className="profile-avatar bg-primary bg-opacity-10 text-primary mb-2">
                                        <FaUserCircle size={24} />
                                    </div>
                                    <h4 className="fw-bold mb-1" style={{ fontSize: "1.1rem" }}>{safeText(profile?.name)}</h4>
                                    {profile?.status !== undefined && (
                                        <span className={`badge ${profile?.status ? "bg-success" : "bg-danger"} px-2 py-1 mt-1`} style={{ fontSize: "0.75rem" }}>
                                            {profile?.status ? (
                                                <>
                                                    <FaCheckCircle className="me-1" size={10} /> Active User
                                                </>
                                            ) : (
                                                <>
                                                    <FaTimesCircle className="me-1" size={10} /> Inactive User
                                                </>
                                            )}
                                        </span>
                                    )}
                                </div>

                                {/* Profile Information */}
                                <div className="mb-3">
                                    <h6 className="fw-bold text-primary mb-2" style={{ fontSize: "0.9rem" }}>Profile Information</h6>
                                    <div className="list-group list-group-flush">
                                        {/* Refer Code */}
                                        <div className="info-row">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-1">
                                                    <FaLink className="text-primary" size={12} />
                                                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>Refer Code</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <code className="bg-light px-1 py-1 rounded" style={{ fontSize: "0.8rem" }}>{safeText(profile?.refer_code)}</code>
                                                    <CopyBtn value={profile?.refer_code} label="Refer code copied" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Mobile */}
                                        <div className="info-row">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-1">
                                                    <FaPhoneAlt className="text-primary" size={12} />
                                                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>Mobile</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <span className="fw-semibold" style={{ fontSize: "0.85rem" }}>{mobileDisplay}</span>
                                                    <CopyBtn value={profile?.mobile || ""} label="Mobile copied" disabled={!profile?.mobile} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="info-row">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-1">
                                                    <FaEnvelope className="text-primary" size={12} />
                                                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>Email</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <span className="fw-semibold" style={{ maxWidth: "150px", wordBreak: "break-word", fontSize: "0.85rem" }}>
                                                        {safeText(profile?.email)}
                                                    </span>
                                                    <CopyBtn value={profile?.email || ""} label="Email copied" disabled={!profile?.email} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Password */}
                                        <PasswordRow password={profile?.password} />
                                    </div>
                                </div>

                                {/* Team Network Section */}
                                <TeamNetworkProfile team={data?.team} />

                                {/* Password Change Form */}
                                <PasswordChangeForm username={username} onUpdateSuccess={fetchProfile} />

                                {/* Wallet Address Section - Only show if has_wallet_address is true */}
                                {data?.has_wallet_address && (
                                    <>
                                        {data?.wallet_address && <WalletAddressDisplay walletAddress={data.wallet_address} />}
                                        <WithdrawalWalletForm username={username} onUpdateSuccess={fetchProfile} />
                                    </>
                                )}

                                {/* Upline Section */}
                                {data?.has_upline && (
                                    <div className="mt-3">
                                        <h6 className="fw-bold text-primary mb-2 d-flex align-items-center" style={{ fontSize: "0.9rem" }}>
                                            <FaUserTie className="me-1" size={12} />
                                            Upline Information
                                        </h6>
                                        <div className="card border-0 bg-light">
                                            <div className="card-body p-2">
                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                    <div>
                                                        <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>{safeText(data?.upline?.name)}</div>
                                                    </div>
                                                    <span className={`badge ${data?.upline?.status ? "bg-success" : "bg-danger"}`} style={{ fontSize: "0.75rem" }}>
                                                        {data?.upline?.status ? "Active" : "Inactive"}
                                                    </span>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm flex-grow-1"
                                                        onClick={() => setUplineOpen(true)}
                                                        style={{ fontSize: "0.8rem" }}
                                                    >
                                                        <FaInfoCircle className="me-1" size={10} /> View Details
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() =>
                                                            navigate(`/user-profile/${encodeURIComponent(data?.upline?.username || "")}`)
                                                        }
                                                        title="Open upline profile"
                                                    >
                                                        <FaUserTie size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Statistics + History */}
                    <div className="col-12 col-xl-8">
                        {/* Balances Section (no collapse) */}
                        <div className="card profile-card mb-3">
                            <div className="card-body p-3">
                                <div className="section-header">
                                    <h4 className="fw-bold text-dark d-flex align-items-center gap-1 mb-0" style={{ fontSize: "1rem" }}>
                                        <FaWallet className="text-primary" size={14} />
                                        Balance Overview
                                    </h4>
                                </div>
                                <div className="pt-2">
                                    <div className="row g-2">
                                        {balanceStats.map((stat, index) => (
                                            <StatTile key={index} {...stat} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trade Statistics (collapse) */}
                        <SectionCard
                            title="Trade Statistics"
                            icon={<FaChartBar size={14} />}
                            open={openTrade}
                            onToggle={() => setOpenTrade((v) => !v)}
                        >
                            <div className="row g-2">
                                {tradeStats.map((stat, index) => (
                                    <StatTile key={index} {...stat} />
                                ))}
                            </div>
                        </SectionCard>

                        {/* Transaction History (collapse) */}
                        <SectionCard
                            title="Transaction History"
                            icon={<FaExchangeAlt size={14} />}
                            open={openTxn}
                            onToggle={() => setOpenTxn((v) => !v)}
                        >
                            <div className="row g-2">
                                {transactionStats.map((stat, index) => (
                                    <StatTile key={index} {...stat} />
                                ))}
                            </div>
                        </SectionCard>

                        {/* Deposit History (new + collapse) */}
                        <SectionCard
                            title="Deposit History"
                            icon={<FaMoneyBillWave size={14} />}
                            open={openDeposits}
                            onToggle={() => {
                                setOpenDeposits((v) => !v);
                                if (!openDeposits && depRows.length === 0) {
                                    fetchDeposits({ page: 1, append: false });
                                }
                            }}
                            right={
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fetchDeposits({ page: 1, append: false });
                                    }}
                                    disabled={depLoading}
                                    title="Refresh deposits"
                                    style={{ fontSize: "0.8rem" }}
                                >
                                    <FaSyncAlt className={depLoading ? "fa-spin" : ""} size={10} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </button>
                            }
                        >
                            <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                                <table className="table table-sm table-striped align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: "0.8rem" }}>Date</th>
                                            <th style={{ fontSize: "0.8rem" }}>Amount</th>
                                            <th style={{ fontSize: "0.8rem" }}>Method</th>
                                            <th style={{ fontSize: "0.8rem" }}>Status</th>
                                            <th className="text-center" style={{ width: 40, fontSize: "0.8rem" }}>
                                                <FaInfoCircle title="Info" size={12} />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {depRows?.length ? (
                                            depRows.map((r, idx) => (
                                                <tr key={`${r?.ref_no || ""}_${idx}`}>
                                                    <td style={{ fontSize: "0.8rem" }}>{formatDateTime(r?.date)}</td>
                                                    <td className="fw-semibold" style={{ fontSize: "0.8rem" }}>{nfmt(r?.amount, 2)}</td>
                                                    <td className="text-uppercase" style={{ fontSize: "0.8rem" }}>{safeText(r?.method)}</td>
                                                    <td>
                                                        <StatusBadge status={r?.status} />
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary px-1 py-0"
                                                            onClick={() => openDetails("deposit", r)}
                                                            title="View details"
                                                        >
                                                            <FaInfoCircle size={12} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="text-center text-muted py-3" style={{ fontSize: "0.85rem" }}>
                                                    {depLoading ? "Loading..." : "No deposits found"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-2">
                                <div className="text-muted small" style={{ fontSize: "0.75rem" }}>
                                    Total: {nfmt(depMeta?.total || 0)} | Page: {depPage}
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary px-2 py-1"
                                    disabled={depLoading || !depHasMore}
                                    onClick={() => fetchDeposits({ page: depPage + 1, append: true })}
                                    style={{ fontSize: "0.8rem" }}
                                >
                                    {depLoading ? "Loading..." : depHasMore ? "Load More" : "No More"}
                                </button>
                            </div>
                        </SectionCard>

                        {/* Withdrawal History (new + collapse) */}
                        <SectionCard
                            title="Withdrawal History"
                            icon={<FaExchangeAlt size={14} />}
                            open={openWithdrawals}
                            onToggle={() => {
                                setOpenWithdrawals((v) => !v);
                                if (!openWithdrawals && wdRows.length === 0) {
                                    fetchWithdrawals({ page: 1, append: false });
                                }
                            }}
                            right={
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fetchWithdrawals({ page: 1, append: false });
                                    }}
                                    disabled={wdLoading}
                                    title="Refresh withdrawals"
                                    style={{ fontSize: "0.8rem" }}
                                >
                                    <FaSyncAlt className={wdLoading ? "fa-spin" : ""} size={10} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </button>
                            }
                        >
                            <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                                <table className="table table-sm table-striped align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: "0.8rem" }}>Date</th>
                                            <th style={{ fontSize: "0.8rem" }}>Amount</th>
                                            <th style={{ fontSize: "0.8rem" }}>Status</th>
                                            <th className="text-center" style={{ width: 40, fontSize: "0.8rem" }}>
                                                <FaInfoCircle title="Info" size={12} />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {wdRows?.length ? (
                                            wdRows.map((r, idx) => (
                                                <tr key={`${r?.date || ""}_${idx}`}>
                                                    <td style={{ fontSize: "0.8rem" }}>{formatDateTime(r?.date)}</td>
                                                    <td className="fw-semibold" style={{ fontSize: "0.8rem" }}>{nfmt(r?.amount, 2)}</td>
                                                    <td>
                                                        <StatusBadge status={r?.status} />
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary px-1 py-0"
                                                            onClick={() => openDetails("withdrawal", r)}
                                                            title="View details"
                                                        >
                                                            <FaInfoCircle size={12} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted py-3" style={{ fontSize: "0.85rem" }}>
                                                    {wdLoading ? "Loading..." : "No withdrawals found"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-2">
                                <div className="text-muted small" style={{ fontSize: "0.75rem" }}>
                                    Total: {nfmt(wdMeta?.total || 0)} | Page: {wdPage}
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary px-2 py-1"
                                    disabled={wdLoading || !wdHasMore}
                                    onClick={() => fetchWithdrawals({ page: wdPage + 1, append: true })}
                                    style={{ fontSize: "0.8rem" }}
                                >
                                    {wdLoading ? "Loading..." : wdHasMore ? "Load More" : "No More"}
                                </button>
                            </div>
                        </SectionCard>
                    </div>
                </div>
            )}

            <UplineModal open={uplineOpen} upline={data?.upline} onClose={() => setUplineOpen(false)} />

            <HistoryDetailModal
                open={detailOpen}
                type={detailType === "deposit" ? "deposit" : "withdrawal"}
                item={detailItem}
                onClose={closeDetails}
            />
        </PageTransition>
    );
}